import React, { useEffect, useState, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { t } from "i18next";
import {Button, Card, Checkbox, Form, Input, InputNumber, Select, Space, Table, Tooltip, Typography, Row, Col, Divider, Tag, Popconfirm} from "antd";
import DatePicker from "../../core/components/DatePicker";
import {
    SaveOutlined,
    DeleteOutlined,
    EditOutlined,
    CreditCardOutlined,
    SwapOutlined,
    GlobalOutlined
} from "@ant-design/icons";
import moment from "moment";
import ApiService from "../../core/service/ApiService";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import Helper from "../../core/Helper";
import CashRegisterSelectList from "../cash-register/CashRegisterSelectList";
import CurrencyService from "../../core/service/CurrencyService";
import CurrencySelect from "../../core/components/CurrencySelect";
import AppConfig from "../../core/service/AppConfig";
import { useModal } from "../../core/context/ModalContext";
import PosTransfer from "./PosTransfer";
import PosCancel from "./PosCancel";
import OperationsPreview from "../appointment/preview/OperationsPreview";
import { PaymentSummaryCard } from "./PaymentSummaryCard";
import { Branding } from "../../core/service/BrandingService";

const { Text } = Typography;

export const Currency = {
    'TRY': '₺',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
}

export default function MultiplePaymentApp({ idList, onSave, onDelete, onCancel, patientId }) {
    const [operations, setOperations] = useState([]);
    const [otherOperations, setOtherOperations] = useState([]);
    const methodsRef = useRef([])
    const registersRef = useRef([])
    const [methods, setMethods] = useState([])
    const { hasPermission, formatPrice } = useUser();
    const formRef = useRef();
    const [payments, setPayments] = useState([]);
    const [appointmentIdList, setAppointmentIdList] = useState(idList || []);
    const [currency, setCurrency] = useState('TRY')
    const [appointmentCurrency, setAppointmentCurrency] = useState('TRY')
    const [currencyValues, setCurrencyValues] = useState([])
    const [isEdit, setIsEdit] = useState(true);
    // Düzenlenen ödeme PAVO POS'a gönderilmiş mi? (tutar alanları kilitlenir)
    const [posLocked, setPosLocked] = useState(false);
    const { openModal, closeModal } = useModal()
    const { user } = useUser()

    // PAVO POS'a başarıyla gönderilmiş ve henüz iptal edilmemiş ödeme
    const isPosCompleted = (row) => !!row?.posTransactionId && !row?.posError

    const getPayments = async () => {
        if (appointmentIdList) {
            const res = await ApiService.Payment.GetPaymentsByAppointmentIds(appointmentIdList, true);
            if (res.success) setPayments(res.data)
        }
    }

    const deletePayment = async (paymentId, appointmentId) => {
        const res = await ApiService.Payment.DeletePayment(appointmentId, paymentId);
        if (res.success) {
            await getPayments();
            formRef.current.resetFields()
            setPosLocked(false)
            onDelete && onDelete()
        }
    }

    const renderActionRow = (row) => {
        // Silinmiş ödeme salt okunur; düzenleme/silme/POS işlemi yapılamaz
        if (row?.isDeleted) return null
        const posCompleted = isPosCompleted(row)
        return (
        <Space size={2}>
            {Branding.feature('posEnable') && (
                posCompleted ? (
                    <Tooltip title={t('POS Cancel')}>
                        <Button size="small" icon={<CreditCardOutlined />} danger onClick={() => openModal('POS Cancel', <PosCancel appointmentId={row?.appointmentId} patientId={patientId} payment={row} onSuccess={async () => { closeModal(); await getPayments() }} />, () => { })} />
                    </Tooltip>
                ) : (
                    <Tooltip title={t('POS Transfer')}>
                        <Button size="small" icon={<SwapOutlined />} onClick={() => openModal('POS Transfer', <PosTransfer appointmentId={row?.appointmentId} patientId={patientId} payment={row} onSuccess={async () => { closeModal(); await getPayments() }} />, () => { })} />
                    </Tooltip>
                )
            )}
            {(!Helper.Date.isBeforeToday(row.paymentDate) || hasPermission(PERMISSIONS.EDIT_PAST_PAYMENT)) && hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT) && (
                <Tooltip title={posCompleted ? t('This payment was sent to the POS, the amount cannot be changed. Cancel it on the POS first.') : t('Edit')}>
                    <Button size="small" type="link" icon={<EditOutlined />} onClick={() => {
                        setIsEdit(false)
                        setPosLocked(posCompleted)
                        let payment = { ...row }
                        payment.paymentDate = moment(new Date(payment.paymentDate))
                        setCurrency(payment?.currency)
                        formRef.current.setFieldsValue({ ...payment })
                    }} />
                </Tooltip>
            )}
            {(!Helper.Date.isBeforeToday(row.paymentDate) || hasPermission(PERMISSIONS.EDIT_PAST_PAYMENT)) && hasPermission(PERMISSIONS.PAYMENT_DELETE) && (
                posCompleted ? (
                    <Tooltip title={t('This payment was sent to the POS, it cannot be deleted before the POS cancellation.')}>
                        <span style={{ cursor: 'not-allowed' }}>
                            <Button size="small" type="link" danger disabled icon={<DeleteOutlined />} style={{ pointerEvents: 'none' }} />
                        </span>
                    </Tooltip>
                ) : (
                    <Popconfirm title={t('Delete?')} onConfirm={() => deletePayment(row.id, row.appointmentId)}>
                        <Button size="small" type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                )
            )}
        </Space>
        )
    }

    const getAppointments = async () => {
        if (appointmentIdList.length > 0) {
            let _operations = [], _otherOperations = []
            for (const id of appointmentIdList) {
                const res = await ApiService.Appointment.GetAppointment(id);
                setAppointmentCurrency(res.data.currency); setCurrency(res.data.currency);
                formRef.current.setFieldsValue({ currency: res.data.currency })
                if (res.success) {
                    _operations = [..._operations, ...res.data.appointmentOperations.map(x => ({ ...x, appointmentId: id, startDate: res.data.startDate, currency: res.data.currency }))]
                    _otherOperations = [..._otherOperations, ...res.data.appointmentOtherOperations.map(x => ({ ...x, appointmentId: id, startDate: res.data.startDate, currency: res.data.currency }))]
                }
            }
            setOperations(_operations);
            setOtherOperations(_otherOperations)
        } else {
            setOperations([]); setOtherOperations([])
        }
    }

    const loadPaymentMethods = async (firmId) => {
        if (methodsRef.current.length === 0) {
            const res = await ApiService.PaymentMethod.GetPaymentMethodList();
            if (res.success) methodsRef.current = res.data
        }
        if (firmId) {
            setMethods(methodsRef.current.filter(x => x?.firm?.id === firmId))
            formRef.current.setFieldsValue({ paymentMethodId: null })
        }
    }

    const onFinish = async (formData) => {
        setIsEdit(true); setCurrency(appointmentCurrency); setPosLocked(false)
        if (formData.diffPayment) {
            formData.amount = Math.abs(formData.amount) * -1
            formData.currencyAmount = Math.abs(formData.currencyAmount ?? 0) * -1
            formData.description = `${formData.description ? formData.description + ' - ' : ''}Diff.`
        }
        if (formData.id) {
            const res = await ApiService.Payment.EditPayment(formData.appointmentId, formData.id, { ...formData })
            if (res.success) { await getPayments(); formRef.current.resetFields() }
        } else {
            const res = await ApiService.Payment.AddPayments(appointmentIdList, { ...formData })
            if (res.success) { await getPayments(); formRef.current.resetFields() }
        }
        onSave && onSave()
    }

    useEffect(() => { getAppointments(); getPayments() }, [appointmentIdList])
    useEffect(() => { setAppointmentIdList(idList) }, [idList]);
    useEffect(() => {
        const load = async () => {
            const res = await CurrencyService.getPriceTRY(['USD', 'EUR', 'GBP'])
            setCurrencyValues(res)
        }
        load()
    }, []);

    // --- STYLES ---
    const compactItemStyle = { marginBottom: 0 };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>

            {/* 1. ÜST KISIM: İŞLEMLER VE FORM */}
            <Row gutter={[12, 12]}>
                {/* Sol Taraf: İşlemler Listesi */}
                <Col xs={24} lg={24}>
                    <Card title={t('Operations')} size="small" bodyStyle={{ padding: 0, maxHeight: 300, overflowY: 'auto' }}>
                        <OperationsPreview operations={operations} otherOperations={otherOperations} />
                    </Card>
                </Col>

                {/* Sağ Taraf: Ödeme Formu (Yatay Flex) */}
                {hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT) && (
                    <Col xs={24}>
                        <Card
                            title={
                                <Space size={8}>
                                    {isEdit ? t('New Payment') : t('Edit Payment')}
                                    {posLocked && (
                                        <Tag color="red" style={{ margin: 0, fontWeight: 'normal' }}>
                                            <CreditCardOutlined /> {t('This payment was sent to the POS, the amount cannot be changed. Cancel it on the POS first.')}
                                        </Tag>
                                    )}
                                </Space>
                            }
                            size="small"
                            style={{ backgroundColor: '#f9f9f9' }}
                            bodyStyle={{ padding: '12px' }}
                            extra={
                                <Button type="link" size="small" onClick={() => {
                                    formRef.current.resetFields();
                                    formRef.current.setFieldsValue({ paymentDate: moment(), amount: 0, currency: appointmentCurrency });
                                    setCurrency(appointmentCurrency);
                                    setIsEdit(true);
                                    setPosLocked(false);
                                }}>{t('Reset')}</Button>
                            }
                        >
                            <Form
                                size={'small'}
                                ref={formRef}
                                onFinish={onFinish}
                                layout={'vertical'}
                                onValuesChange={({ currency, cashRegisterId }) => {
                                    if (currency) setCurrency(currency)
                                    else if (cashRegisterId) {
                                        const reg = registersRef.current.find(x => x.id === cashRegisterId)
                                        formRef.current.setFieldsValue({ paymentMethodId: null })
                                        loadPaymentMethods(reg?.firmId)
                                    }
                                }}
                                initialValues={{ amount: 0, paymentDate: moment(), currency: currency, differencePayment: 0, currencyAmount: 0 }}
                            >
                                <Form.Item name={'id'} hidden /><Form.Item name={'appointmentId'} hidden />

                                {/* YATAY FLEX KONTEYNER */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>

                                    {/* Tarih */}
                                    <div style={{ width: 125 }}>
                                        <Form.Item label={t('Date')} name={'paymentDate'} style={compactItemStyle}>
                                            <DatePicker showTime format={'DD/MM HH:mm'} style={{ width: '100%' }} disabled={!hasPermission(PERMISSIONS.PAYMENT_EDIT_DATE)} locale={Helper.Date.getLocale()} />
                                        </Form.Item>
                                    </div>

                                    {/* Kasa */}
                                    <div style={{ width: 140 }}>
                                        <Form.Item label={t('Register')} name={'cashRegisterId'} rules={[{ required: true }]} style={compactItemStyle}>
                                            <CashRegisterSelectList currency={currency} style={{ width: '100%' }} onList={(registers) => {
                                                registersRef.current = registers
                                                let reg = user?.userCashRegisters?.find(x => x.isDefault) ?? (user?.userCashRegisters?.length > 0 && user?.userCashRegisters[0])
                                                const exist = registers.find(x => x.id === reg?.cashRegisterId)
                                                formRef.current.setFieldsValue({ cashRegisterId: exist?.id })
                                                loadPaymentMethods(exist?.firmId)
                                            }} all={false} />
                                        </Form.Item>
                                    </div>

                                    {/* Yöntem */}
                                    <div style={{ width: 140 }}>
                                        <Form.Item label={t('Method')} name={'paymentMethodId'} rules={[{ required: true }]} style={compactItemStyle}>
                                            <Select options={methods} fieldNames={{ label: 'name', value: 'id' }} style={{ width: '100%' }} placeholder={t('Select')} />
                                        </Form.Item>
                                    </div>

                                    {/* Para Birimi */}
                                    <div style={{ width: 100 }}>
                                        <Form.Item label={t('Curr.')} name={'currency'} style={compactItemStyle}>
                                            <CurrencySelect style={{ width: '100%' }} disabled={posLocked} />
                                        </Form.Item>
                                    </div>

                                    {/* Tutar */}
                                    <div style={{ width: 100 }}>
                                        <Form.Item label={t('Amount')} name={'amount'} rules={[{ required: true }]} style={compactItemStyle}>
                                            <InputNumber style={{ width: '100%', fontWeight: 'bold' }} prefix={Currency[currency]} disabled={posLocked} />
                                        </Form.Item>
                                    </div>

                                    {/* Karşılık (Varsa) */}
                                    {appointmentCurrency !== currency && (
                                        <div style={{ width: 100 }}>
                                            <Form.Item label={`${t('Eq.')}(${appointmentCurrency})`} name={'currencyAmount'} rules={[{ required: true }]} style={compactItemStyle}>
                                                <InputNumber style={{ width: '100%' }} prefix={Currency[appointmentCurrency]} disabled={posLocked} />
                                            </Form.Item>
                                        </div>
                                    )}

                                    <div style={{}}>
                                        <Form.Item name={'diffPayment'} valuePropName={'checked'} style={compactItemStyle}>
                                            <Checkbox disabled={posLocked}>{t('Diff')}</Checkbox>
                                        </Form.Item>
                                    </div>

                                    {/* Checkbox */}
                                    <div>

                                        {/* Açıklama (Esnek) */}
                                        <div style={{ flex: 1, minWidth: 150 }}>
                                            <Form.Item name={'description'} style={compactItemStyle}>
                                                <Input placeholder={t('Desc...')} />
                                            </Form.Item>
                                        </div>
                                    </div>

                                    {/* Buton */}
                                    <div>
                                        <Form.Item style={compactItemStyle}>
                                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                                {t('Save')}
                                            </Button>
                                        </Form.Item>
                                    </div>
                                </div>
                            </Form>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* 2. ORTA KISIM: ÖDEME GEÇMİŞİ */}
            <Card size="small" title={t('History')} bodyStyle={{ padding: 0 }}>
                <style>{`
                    .payment-row-deleted > td { background-color: #fafafa !important; opacity: .55; }
                    .payment-row-deleted:hover > td { background-color: #f0f0f0 !important; }
                `}</style>
                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={18}>
                        <Table
                            scroll={{ x: 600 }}
                            size={'small'}
                            dataSource={payments}
                            rowKey={'id'}
                            rowClassName={(row) => row?.isDeleted ? 'payment-row-deleted' : ''}
                            pagination={false}
                            columns={[
                                { key: 'appointmentId', dataIndex: 'appointmentId', title: t('ID'), width: 60 },
                                {
                                    key: 'paymentInfo', title: t('Payment Info'), width: 250, render: (_, row) => (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Text style={{ fontSize: 12, color: '#666' }}>{Helper.Date.dateFormat(row.paymentDate)}</Text>
                                                <Text style={{ fontSize: 12 }}>{row.cashRegister.name}</Text>
                                                <Tag style={{ margin: 0, fontSize: 11 }}>{row.paymentMethod.name}</Tag>
                                                {row.isDeleted && <Tag color="default" style={{ margin: 0, fontSize: 11 }}>{t('Deleted')}</Tag>}
                                            </div>
                                            {row.description && <Text type="secondary" style={{ fontSize: 11 }}>{row.description}</Text>}
                                        </div>
                                    )
                                },
                                { key: 'amount', dataIndex: 'amount', title: `${t('Amount')} (${appointmentCurrency})`, align: 'right', width: 110, render: (text, row) => <Text strong delete={row?.isDeleted}>{formatPrice(row.currencyAmount, { currency: appointmentCurrency })}</Text> },
                                { key: 'currency', dataIndex: 'currency', title: t('Received'), align: 'right', width: 110, render: (text, row) => <Text type="secondary" delete={row?.isDeleted}>{formatPrice(row.amount, { currency: row?.currency })}</Text> },
                                { key: 'action', title: '', render: (_, row) => renderActionRow(row) },
                            ]}
                        />
                    </Col>
                    <Col xs={24} xl={6}>
                        <PaymentSummaryCard
                            payments={payments.filter(x => !x.isDeleted)}
                            currency={appointmentCurrency}
                            totalAmount={(operations?.reduce((prev, val) => prev += val?.price, 0) || 0) + (otherOperations?.reduce((prev, val) => prev += val.price, 0) || 0)}
                        />
                    </Col>
                </Row>
            </Card>

            {/* 3. ALT KISIM: DÖVİZ */}
            {currencyValues.length > 0 && (
                <div style={{ backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: 4, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Space><GlobalOutlined style={{ color: '#1890ff' }} /><Text strong style={{ fontSize: 12 }}>{t('Rates')}:</Text></Space>
                    <Divider type="vertical" />
                    {currencyValues.map((c, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Tag color="blue" style={{ margin: 0 }}>{c.currency}</Tag><Text style={{ fontSize: 12 }}>{c.value}₺</Text>
                            {i < currencyValues.length - 1 && <Divider type="vertical" />}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}